import React from 'react';
import Container from '@material-ui/core/Container';
import Typography from '@material-ui/core/Typography';
import Box from '@material-ui/core/Box';
import NavBar from '../components/NavBar';
import Contact from '../components/Contact';
import Grid from '@material-ui/core/Grid';
import { makeStyles } from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import Link from '../components/Link';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronRight, faHome } from '@fortawesome/free-solid-svg-icons';

import { graphql, useStaticQuery } from 'gatsby';
import Img from 'gatsby-image';

const useStyles = makeStyles((theme) => ({
  header: {
    color: theme.palette.background.default,
    textAlign: 'center',
    fontWeight: 500,
    background: theme.palette.primary.main,
    backgroundImage: `repeating-linear-gradient(135deg, ${theme.palette.primary.dark} 0, ${theme.palette.primary.dark} 1px, transparent 1px, transparent 30px)`,
    zIndex: -10,
    marginBottom: '20vh',
  },
  border: {
    width: 620,
    padding: 10,
    borderBottom: '1px solid #bf563d',
  },
  root: {
    flexGrow: 1,
    margin: 75,
  },
  titleBorder: {
    borderLeft: '1px solid #bf563d',
    paddingLeft: 10,
  },
  subtitleBorder: {
    borderBottom: '1px solid #bf563d',
    paddingBottom: 10,
  },
  stripes: {
    color: theme.palette.background.default,
    paddingTop: theme.spacing(2),
    margin: 0,
    paddingBottom: theme.spacing(2),
    backgroundImage: `linear-gradient(to bottom right, #FFF 50%, ${theme.palette.background.default} 50%)`,
    zIndex: -10,
    marginBottom: theme.spacing(1),
  },
  stripesLeft: {
    padding: 0,
    margin: 0,
    height: '25vh',
    opacity: '25%',
    backgroundImage:
      'repeating-linear-gradient(45deg, #336666 0, #336666 3px, transparent 3px, transparent 30px)',
    transform: 'skewY(5deg)',
  },
  stripesRight: {
    padding: 0,
    margin: 0,
    height: '15vh',
    opacity: '25%',
    backgroundImage:
      'repeating-linear-gradient(45deg, #336666 0, #336666 3px, transparent 3px, transparent 30px)',
    transform: 'skewY(-5deg)',
  },
  media: {
    display: 'block',
    position: 'absolute',
    bottom: '-40%',
    // zIndex: 10,
    left: 0,
    right: 0,
  },
  marginRight: {
    marginRight: theme.spacing(2),
    marginBottom: theme.spacing(4),
  },
  marginLeft: {
    marginLeft: theme.spacing(2),
    marginBottom: theme.spacing(4),
  },
  align: {
    textAlign: 'center',
    margin: theme.spacing(1),
  },
  link: {
    color: '#1f3536',
    textDecoration: 'none',
  },
  projectNum: {
    paddingTop: theme.spacing(2),
    fontFamily: 'Roboto Condensed',
    fontKerning: 5,
    textAlign: 'center',
    color: theme.palette.primary.main,
    fontWeight: 500,
    paddingBottom: theme.spacing(2),
  },
}));

export default function Opus() {
  const data = useStaticQuery(graphql`
    query {
      opus: file(relativePath: { eq: "Opus.png" }) {
        id
        childImageSharp {
          fixed {
            ...GatsbyImageSharpFixed
          }
          fluid(maxWidth: 400) {
            ...GatsbyImageSharpFluid
          }
        }
      }
    }
  `);

  const classes = useStyles();

  return (
    <>
      <Container maxWidth="xl">
        <Box my={5}>
          <NavBar />
        </Box>
      </Container>
      <Box
        position="relative"
        m={0}
        p={0}
        maxWidth="100vw"
        maxHeight="75vh"
        className={classes.stripes}
      >
        <Box
          display="flex"
          justifyContent="space-evenly"
          flexDirection="column"
          alignItems="center"
        >
          <Box>
            <Typography variant="h1" className={classes.projectNum}>
              OPUS
            </Typography>
          </Box>
          <Container maxWidth="md">
            <Img
              alt="opus website screenshot"
              fluid={data.opus.childImageSharp.fluid}
            />
          </Container>
        </Box>
      </Box>

      <Box className={classes.root}>
        <Grid container spacing={6} justify="center" direction="row">
          <Grid item xs={12} md={4}>
            <Typography variant="h3" className={classes.align}>
              Overview
            </Typography>
          </Grid>
          <Grid item xs={12} md={8} className={classes.titleBorder}>
            <Typography variant="h5" gutterBottom>
              For our capstone project, my group built Opus, an interactive web
              application that provides piano lessons, exercises and songs for
              the beginner pianist. I had the pleasure of working with three
              other developers over the course of 15 days to bring the MVP to
              fruition.
            </Typography>
            <Typography variant="h5" gutterBottom>
              Being given the freedom to create anything, and explore new
              technologies and interests, our group was really passionate about
              music. Being the only person who never took music lessons, I had
              an added bonus to expand my knowledge of music theory. To
              celebrate the small wins too, Opus taught me the difference
              between a chord and scale.
            </Typography>
            <Typography variant="h5" gutterBottom>
              My favorite personal contribution I made to our application was
              the song library. Our team wanted to provide a learning
              environment that allowed the user to play at their own speed and
              visually see what notes will come next in the song. To do this on
              the frontend, I learned the fundamentals of GreenSock's Animation
              Platform &#40;GSAP&#41; to move the hinted-at note of sight once
              the user matched that note on our keyboard. To complete the songs
              feature, I built out the database model, API routes and reducers
              for our store so we could dynamically load in the correct song
              based on whichever page the user visited.
            </Typography>
          </Grid>
        </Grid>
      </Box>
      <Box className={classes.stripesLeft}></Box>
      <Box className={classes.root}>
        <Grid container spacing={6} justify="center" direction="row">
          <Grid item xs={12} md={4}>
            <Typography variant="h3" className={classes.align}>
              Tech Stack
            </Typography>
          </Grid>
          <Grid item xs={12} md={8} className={classes.titleBorder}>
            <Box pb={2}>
              <Typography
                variant="h4"
                gutterBottom
                className={classes.subtitleBorder}
              >
                Language & Environment
              </Typography>
              <Typography variant="h5" gutterBottom>
                JavaScript
              </Typography>
              <Typography variant="h5" gutterBottom>
                Node.js
              </Typography>
            </Box>
            <Box pb={2}>
              <Typography
                variant="h4"
                gutterBottom
                className={classes.subtitleBorder}
              >
                Backend
              </Typography>
              <Typography variant="h5" gutterBottom>
                Express.js
              </Typography>
              <Typography variant="h5" gutterBottom>
                PostgreSQL
              </Typography>
              <Typography variant="h5" gutterBottom>
                Sequelize
              </Typography>
            </Box>
            <Box pb={2}>
              <Typography
                variant="h4"
                gutterBottom
                className={classes.subtitleBorder}
              >
                Frontend
              </Typography>
              <Typography variant="h5" gutterBottom>
                React.js
              </Typography>
              <Typography variant="h5" gutterBottom>
                Redux
              </Typography>
              <Typography variant="h5" gutterBottom>
                Bootstrap Framework
              </Typography>
              <Typography variant="h5" gutterBottom>
                Styled Components
              </Typography>
              <Typography variant="h5" gutterBottom>
                Howler.js
              </Typography>
              <Typography variant="h5" gutterBottom>
                GSAP
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Box>
      <Container maxWidth="sm">
        <Box display="flex" justifyContent="center">
          <Button
            className={classes.marginRight}
            variant="contained"
            color="secondary"
            size="large"
            href="http://www.opuslesson.com/"
            target="blank"
          >
            DEMO
          </Button>
          <Button
            className={classes.marginLeft}
            variant="outlined"
            size="large"
            color="secondary"
            href="https://github.com/2001-northstar/Capstone"
            target="blank"
          >
            GITHUB
          </Button>
        </Box>
      </Container>
      <Box className={classes.stripesRight}></Box>
      <Container maxWidth="lg">
        <Box mt={5}>
          <Grid container>
            <Grid item xs={12} sm={4}>
              <Typography variant="h4" className={classes.align}></Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography variant="h4" className={classes.align}>
                <Link to="/" color="secondary">
                  <FontAwesomeIcon icon={faHome}></FontAwesomeIcon>
                </Link>
              </Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography variant="h4" className={classes.align}>
                <Link to="/weatherd" color="secondary">
                  <Box component="span" p={2} className={classes.link}>
                    Weather'd
                  </Box>
                  <FontAwesomeIcon icon={faChevronRight}></FontAwesomeIcon>
                </Link>
              </Typography>
            </Grid>
          </Grid>
        </Box>
      </Container>
      <Box mt={5}>
        <Contact />
      </Box>
    </>
  );
}
